'use client'

import * as React from 'react'

const TAILWIND_BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280
} as const

export type BreakpointTier = 'base' | 'sm' | 'md' | 'lg' | 'xl'

export type BreakpointTiers<T> = {
  base: T
  sm?: T
  md?: T
  lg?: T
  xl?: T
}

function getTierForWidth(width: number): BreakpointTier {
  if (width >= TAILWIND_BREAKPOINTS.xl) return 'xl'
  if (width >= TAILWIND_BREAKPOINTS.lg) return 'lg'
  if (width >= TAILWIND_BREAKPOINTS.md) return 'md'
  if (width >= TAILWIND_BREAKPOINTS.sm) return 'sm'
  return 'base'
}

export function useIsBelow(breakpoint: number): boolean {
  const [isBelow, setIsBelow] = React.useState(false)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
    const onChange = () => setIsBelow(window.innerWidth < breakpoint)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [breakpoint])

  return isBelow
}

export function useBreakpointValue<T extends BreakpointTiers<unknown>>(
  tiers: T
): NonNullable<T[keyof T & BreakpointTier]> {
  const [tier, setTier] = React.useState<BreakpointTier>('base')

  React.useEffect(() => {
    const compute = () => setTier(getTierForWidth(window.innerWidth))
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [])

  const fallbackOrder: BreakpointTier[] = ['xl', 'lg', 'md', 'sm', 'base']
  const startIdx = fallbackOrder.indexOf(tier)
  for (let i = startIdx; i < fallbackOrder.length; i++) {
    const value = tiers[fallbackOrder[i] as keyof T]
    if (value !== undefined) return value as NonNullable<T[keyof T & BreakpointTier]>
  }
  return tiers.base as NonNullable<T[keyof T & BreakpointTier]>
}
